import React, { useState, useEffect } from 'react';
import styles from './PublisherForm.module.css';
import { showErrorToast, showSuccessToast } from '../../components/Shared/Toastify/toastHelper';
import { CircularProgress } from '@mui/material';
import { useWebSettings } from '../../context/WebSettingsContext';
import { socialsDTO } from '../../dto/WebSettingsDTO';

const ContactDetails: React.FC = () => {
    const { webSettings, updateWebSettings, loading: contextLoading } = useWebSettings();
    const [buttonLoading, setButtonLoading] = useState(false);
    const [formData, setFormData] = useState<{
        socials: socialsDTO;
        contact: {
            email: string;
            phone: string;
            location: string;
        };
    }>({
        socials: {
            instagramUrl: '',
            facebookUrl: '',
            whatsappUrl: '',
            XUrl: '',
            youtubeUrl: '',
        },
        contact: {
            email: '',
            phone: '',
            location: '',
        }
    });

    useEffect(() => {
        if (webSettings?.socials && webSettings?.contactDetails && !contextLoading) {
            setFormData({
                socials: {
                    instagramUrl: webSettings.socials.instagramUrl || '',
                    facebookUrl: webSettings.socials.facebookUrl || '',
                    whatsappUrl: webSettings.socials.whatsappUrl || '',
                    XUrl: webSettings.socials.XUrl || '',
                    youtubeUrl: webSettings.socials.youtubeUrl || '',
                },
                contact: {
                    email: webSettings.contactDetails.email || '',
                    phone: webSettings.contactDetails.phone || '',
                    location: webSettings.contactDetails.location || '',
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contextLoading]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setButtonLoading(true);

            await updateWebSettings({
                contactDetails: {
                    email: formData.contact.email,
                    phone: formData.contact.phone,
                    location: formData.contact.location
                },
                socials: {
                    instagramUrl: formData.socials.instagramUrl,
                    facebookUrl: formData.socials.facebookUrl,
                    youtubeUrl: formData.socials.youtubeUrl,
                    whatsappUrl: formData.socials.whatsappUrl,
                    XUrl: formData.socials.XUrl
                }
            });

            showSuccessToast('Contact details and socials updated successfully!');
        } catch (error: any) {
            showErrorToast('Error updating contact details and socials: ' + error.message);
        } finally {
            setButtonLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name in formData.socials) {
            setFormData(prev => ({
                ...prev,
                socials: {
                    ...prev.socials,
                    [name]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                contact: {
                    ...prev.contact,
                    [name]: value
                }
            }));
        }
    };

    const validateForm = (): boolean => {
        const { email, phone } = formData.contact;
        const errors: string[] = [];

        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.push('Invalid email format');
        }

        if (phone && !/^\+?[\d\s-]{8,}$/.test(phone)) {
            errors.push('Invalid phone number format');
        }

        if (errors.length > 0) {
            showErrorToast(errors.join(', '));
            return false;
        }

        return true;
    };

    if (contextLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className={styles.background}>
            <div className={styles.Title}>Social Media</div>
            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.row}>
                    <div className={styles.field}>
                        <label>Facebook</label>
                        <input
                            type="url"
                            name="facebookUrl"
                            value={formData.socials.facebookUrl}
                            onChange={handleInputChange}
                            className={styles.textField}
                            placeholder="https://facebook.com/example"
                        />
                    </div>
                    <div className={styles.field}>
                        <label>Instagram</label>
                        <input
                            type="url"
                            name="instagramUrl"
                            value={formData.socials.instagramUrl}
                            onChange={handleInputChange}
                            className={styles.textField}
                            placeholder="https://instagram.com/example"
                        />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.field}>
                        <label>Twitter (X)</label>
                        <input
                            type="url"
                            name="XUrl"
                            value={formData.socials.XUrl}
                            onChange={handleInputChange}
                            className={styles.textField}
                            placeholder="https://twitter.com/example"
                        />
                    </div>
                    <div className={styles.field}>
                        <label>Youtube</label>
                        <input
                            type="url"
                            name="youtubeUrl"
                            value={formData.socials.youtubeUrl}
                            onChange={handleInputChange}
                            className={styles.textField}
                            placeholder="https://youtube.com/example"
                        />
                    </div>
                </div>
                <div className={styles.row}>
                    <div className={styles.field}>
                        <label>WhatsApp</label>
                        <input
                            type="url"
                            name="whatsappUrl"
                            value={formData.socials.whatsappUrl}
                            onChange={handleInputChange}
                            className={styles.textField}
                            placeholder="https://wa.me/1234567890"
                        />
                    </div>
                    <div className={styles.field}>
                        <input type="text" className='border-none hidden' disabled={true} />
                    </div>
                </div>

                <div className={styles.Title}>Contact Information</div>
                <div className={styles.row}>
                    <div className={styles.field}>
                        <label>Email Address</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.contact.email}
                            onChange={handleInputChange}
                            className={styles.textField}
                            placeholder="contact@example.com"
                        />
                    </div>
                    <div className={styles.field}>
                        <label>Phone Number</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.contact.phone}
                            onChange={handleInputChange}
                            className={styles.textField}
                            placeholder="+1234567890"
                        />
                    </div>
                </div>
                <div className={styles.field}>
                    <label>Location</label>
                    <input
                        type="text"
                        name="location"
                        value={formData.contact.location}
                        onChange={handleInputChange}
                        className={styles.textField}
                        placeholder="123 Main Street, City, Country"
                    />
                </div>

                <div className={styles.buttonContainer}>
                    <button className={styles.AddButton} type="submit" disabled={buttonLoading}>
                        {buttonLoading ? <CircularProgress style={{ color: 'white' }} /> : 'UPDATE'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ContactDetails;